import React, { useEffect, useState } from "react";
import Sidebar from "../Sidebar/Sidebar";
import Navbar from "./Navbar";
import Header from "./header";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import ApiConfig from "../../common/config/ApiConfig";
import showValidateErrors from "../../common/utils/showValidateErrors";
import { useLanguage } from "../../common/i18n/LanguageContext";

const STATUS_TABS = [
  { key: "pending", labelKey: "topup_status_pending" },
  { key: "approve", labelKey: "topup_status_approved" },
  { key: "reject", labelKey: "topup_status_rejected" },
  { key: "all", labelKey: "common_all" },
];

const STATUS_LABEL_KEY_BY_VALUE = {
  pending: "topup_status_pending",
  approve: "topup_status_approved",
  reject: "topup_status_rejected",
};

const STATUS_CHIP = {
  pending: "bg-yellow-400 text-white",
  approve: "bg-green-500 text-white",
  reject: "bg-red-500 text-white",
};

function MemberWithdraw() {
  const { t } = useLanguage();
  const pos = localStorage.getItem("position");
  const user = localStorage.getItem("user");

  const [withdraws, setWithdraws] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [adminId, setAdminId] = useState(null);
  const [processingId, setProcessingId] = useState(null);

  const fetchAdminId = () => {
    ApiConfig.get(`/agentprofile?user=${encodeURIComponent(user)}`).then(
      (result) => {
        if (result.resCode === 200) {
          setAdminId(result.data.id);
        } else {
          toast.error(result.message || t("topup_load_admin_error"), {
            position: "top-right",
            autoClose: 3000,
          });
        }
      },
    );
  };

  const [currencyCodeById, setCurrencyCodeById] = useState({});
  const fetchCurrencies = () => {
    ApiConfig.get("/getCurrencyList").then((result) => {
      if (result.resCode !== 200) return;
      const list = Array.isArray(result.data) ? result.data : [];
      setCurrencyCodeById(Object.fromEntries(list.map((c) => [c.id, c.code])));
    });
  };

  const fetchWithdraws = () => {
    ApiConfig.get("/getmemberwithdraw").then((result) => {
      if (result.resCode === 200) {
        setWithdraws(Array.isArray(result.data) ? result.data : []);
      } else {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
      }
    });
  };

  useEffect(() => {
    fetchAdminId();
    fetchWithdraws();
    fetchCurrencies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredWithdraws = withdraws.filter((item) =>
    statusFilter === "all" ? true : item.status === statusFilter,
  );

  const handleAction = (row, action) => {
    if (!adminId) {
      toast.error(t("topup_admin_not_found"), {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    const endpoint =
      action === "approve" ? "/approvememberwithdraw" : "/rejectmemberwithdraw";

    const debitAmount = `${parseFloat(row.debit).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${currencyCodeById[row.requestBy?.currency_id] ?? ""}`.trim();

    const confirmTitle =
      action === "approve"
        ? t("withdraw_confirm_approve_title")
        : t("withdraw_confirm_reject_title");
    const confirmText =
      action === "approve"
        ? `${t("withdraw_confirm_approve_text_prefix")} ${debitAmount} ${t(
            "withdraw_confirm_approve_text_middle",
          )} : ${row.requestBy?.Username ?? "-"}`
        : `${t("withdraw_confirm_reject_text")} : ${row.requestBy?.Username ?? "-"}`;

    Swal.fire({
      title: confirmTitle,
      text: confirmText,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: t("common_confirm"),
      cancelButtonText: t("common_cancel"),
    }).then((result) => {
      if (!result.isConfirmed) return;

      setProcessingId(row.id);
      ApiConfig.post(endpoint, { id: row.id, approveBy: adminId })
        .then((result) => {
          if (result.resCode === 422) {
            const messages = Array.isArray(result.errors)
              ? result.errors.flatMap((err) => err.message)
              : [];
            const insufficientCredit = messages.includes(
              "Cannot approve: insufficient member credit",
            );

            if (insufficientCredit) {
              toast.error(t("withdraw_insufficient_credit"), {
                position: "top-right",
                autoClose: 3000,
              });
            } else if (messages.length > 0) {
              showValidateErrors(result.errors);
            } else {
              toast.error(result.message || t("topup_already_processed"), {
                position: "top-right",
                autoClose: 3000,
              });
            }
            fetchWithdraws();
            return;
          }
          if (result.resCode !== 200) {
            toast.error(result.message || t("common_generic_error"), {
              position: "top-right",
              autoClose: 3000,
            });
            return;
          }
          toast.success(
            action === "approve"
              ? t("topup_approve_success")
              : t("topup_reject_success"),
            { position: "top-right", autoClose: 3000 },
          );
          fetchWithdraws();
        })
        .catch(() => {
          toast.error(t("topup_action_error"), {
            position: "top-right",
            autoClose: 3000,
          });
        })
        .finally(() => setProcessingId(null));
    });
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <div className="shrink-0">
        <Navbar />
      </div>
      <div className="flex flex-1 overflow-hidden items-start">
        <div className="shrink-0 overflow-y-auto h-full">
          <Sidebar pos={pos} />
        </div>
        <div className="flex flex-col flex-1 max-h-full overflow-hidden">
          <div className="shrink-0">
            <Header title={t("menu_manage_withdraw")} />
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            <div className="flex flex-row gap-2 mb-2">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setStatusFilter(tab.key)}
                  className={`py-1 px-3 rounded text-xs font-bold ${
                    statusFilter === tab.key
                      ? "bg-brand-blue-700 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  {t(tab.labelKey)}
                </button>
              ))}
            </div>

            <div className="overflow-x-auto rounded">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-brand-blue-800 text-white">
                    <th className="px-2 py-1 text-left">
                      {t("membertable_tx_col_date")}
                    </th>
                    <th className="px-2 py-1 text-left">
                      {t("common_member")}
                    </th>
                    <th className="px-2 py-1 text-left">Agent</th>
                    <th className="px-2 py-1 text-right">
                      {t("withdraw_col_balance")}
                    </th>
                    <th className="px-2 py-1 text-right">
                      {t("withdraw_col_amount")}
                    </th>
                    <th className="px-2 py-1 text-center">
                      {t("subaccount_status_label")}
                    </th>
                    <th className="px-2 py-1 text-left">
                      {t("topup_col_processed_by")}
                    </th>
                    <th className="px-2 py-1 text-center">
                      {t("membertable_col_manage")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredWithdraws.length === 0 ? (
                    <tr>
                      <td
                        colSpan={8}
                        className="text-center py-4 text-gray-400"
                      >
                        {t("common_no_data")}
                      </td>
                    </tr>
                  ) : (
                    filteredWithdraws.map((row, index) => (
                      <tr
                        key={row.id}
                        className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td className="px-2 py-1 text-gray-500">
                          {moment(row.createAt).format("DD/MM/YYYY HH:mm")}
                        </td>
                        <td className="px-2 py-1">
                          <span className="font-bold">
                            {row.requestBy?.Username ?? "-"}
                          </span>
                          <div className="text-[10px] text-gray-500">
                            {row.requestBy?.f_name} {row.requestBy?.l_name}
                          </div>
                        </td>
                        <td className="px-2 py-1">
                          {row.requestBy?.agent ?? "-"}
                        </td>
                        <td className="px-2 py-1 text-right text-sky-600 font-semibold">
                          {parseFloat(
                            row.requestBy?.credit ?? 0,
                          ).toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2,
                          })}{" "}
                          {currencyCodeById[row.requestBy?.currency_id]}
                        </td>
                        <td className="px-2 py-1 text-right font-semibold text-red-600">
                          {parseFloat(row.debit).toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2,
                          })}{" "}
                          {currencyCodeById[row.requestBy?.currency_id]}
                        </td>
                        <td className="px-2 py-1 text-center">
                          <span
                            className={`px-2 py-1 rounded text-[10px] font-bold ${
                              STATUS_CHIP[row.status] ??
                              "bg-gray-400 text-white"
                            }`}
                          >
                            {STATUS_LABEL_KEY_BY_VALUE[row.status]
                              ? t(STATUS_LABEL_KEY_BY_VALUE[row.status])
                              : row.status}
                          </span>
                        </td>
                        <td className="px-2 py-1 text-gray-700">
                          {row.approveBy?.Username ?? "-"}
                        </td>
                        <td className="px-2 py-1 text-center">
                          {row.status === "pending" && (
                            <div className="flex flex-row gap-1 justify-center">
                              <button
                                disabled={processingId === row.id}
                                onClick={() => handleAction(row, "approve")}
                                className="bg-green-500 hover:bg-green-700 disabled:opacity-50 text-white font-bold py-1 px-2 rounded text-xs"
                              >
                                {t("topup_approve_button")}
                              </button>
                              <button
                                disabled={processingId === row.id}
                                onClick={() => handleAction(row, "reject")}
                                className="bg-red-500 hover:bg-red-700 disabled:opacity-50 text-white font-bold py-1 px-2 rounded text-xs"
                              >
                                {t("topup_reject_button")}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MemberWithdraw;
